<template>
  <div
    class="media_wrapper d-flex flex-column align-items-center justify-content-center"
    :id="`media-attachment-${attachmentId}`"
  >
    <video
      v-if="source && isVideo && !isError"
      :src="source"
      :type="contentType"
      :ref="`video-attachment-${attachmentId}`"
      controls
      playsinline
      crossorigin
      :style="{ width: mediaWidth }"
    ></video>
    <audio
      v-else-if="source && isAudio && !isError"
      :src="source"
      :type="contentType"
      :ref="`audio-attachment-${attachmentId}`"
      controls
    ></audio>
    <div v-if="(isError || !source) && isVideo" class="video-placeholder" :style="{ width: mediaWidth }">
      <b-skeleton-img aspect="4:3"></b-skeleton-img>
    </div>
    <div v-if="(isError || !source) && isAudio" class="audio-placeholder">
      <b-skeleton-img></b-skeleton-img>
    </div>
    <div v-if="isError" class="error-msg">
      <a
        :href="source"
        target="_blank"
        class="text-dark text-decoration-underline"
      >
        <font-awesome-icon icon="fas fa-download" />&nbsp; Please click to
        download media.
      </a>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import axios from "axios";
import { mapGetters } from "vuex";
import errorHandler from "@/mixins/errorHandler";

export default {
  name: "MediaItem",
  mixins: [errorHandler],
  props: {
    attachmentId: {
      type: String,
      required: true,
    },
    attachment: {
      type: Object,
      required: true,
    },
    processed: {
      type: Boolean,
      default: false,
    },
    mediaWidth: {
      type: String,
      default: "100%",
    },
  },
  data() {
    return {
      options: {},
      source: null,
      contentType: "",
      isError: false,
      loading: false,
      fetchAbortController: null,
    };
  },
  computed: {
    ...mapGetters("user", ["currentToken"]),
    isVideo() {
      return this.contentType.includes("video");
    },
    isAudio() {
      return this.contentType.includes("audio");
    },
    mediaRef() {
      const mediaRef = this.isAudio
        ? `audio-attachment-${this.attachmentId}`
        : `video-attachment-${this.attachmentId}`;
      return this.$refs[mediaRef];
    },
  },
  created() {
    this.contentType = this.attachment.content_type;
  },
  methods: {
    async loadMedia() {
      try {
        this.loading = true;
        this.fetchAbortController = new AbortController();
        const headers = {
          Authorization: `Bearer ${this.currentToken}`,
        };
        const res = await axios.get(
          `${this.$apihost}/attachments/${this.attachmentId}/download?stream=1`,
          {
            headers: headers,
            responseType: "blob",
            signal: this.fetchAbortController.signal,
          }
        );
        this.source = URL.createObjectURL(res.data);
        this.$nextTick(() => {
          this.watchMediaStatus(this.mediaRef);
        });
      } catch(err) {
        if (!this.isApiCanceled(err)) {
          console.log("[MediaItem:loadMedia] ERROR: ", err);
        }
      } finally {
        this.loading = false;
      }
    },
    watchMediaStatus(mediaRef) {
      if (mediaRef) {
        mediaRef.addEventListener("error", () => {
          this.isError = true;
        });

        mediaRef.addEventListener("canplay", () => {
          this.isError = false;
        });

        mediaRef.addEventListener("canplaythrough", () => {
          this.isError = false;
        });

        mediaRef.addEventListener("loadstart", () => {
          this.isError = false;
        });

        mediaRef.addEventListener("loadeddata", () => {
          this.isError = false;
        });

        mediaRef.addEventListener("loadedmetadata", () => {
          this.isError = false;
        });
      }
    },
  },
  watch: {
    processed: {
      handler(value) {
        if (value) {
          this.loadMedia();
        }
      },
      immediate: true,
    },
  },
  beforeDestroy() {
    if (this.fetchAbortController) {
      this.fetchAbortController.abort();
      this.fetchAbortController = null;
    }
  }
};
</script>

<style lang="scss">
.media_wrapper {
  width: 100%;
  gap: 8px;

  video {
    width: 100%;
    border-radius: 4px;
  }

  .error-msg {
    font-size: 11px;
    text-align: center;
    width: 100%;
  }

  .video-placeholder {
    display: flex;
    justify-content: center;
    width: 100%;
    .b-aspect {
      width: 100%;
      .b-skeleton-img {
        border-radius: 4px;
      }
    }
  }

  .audio-placeholder {
    width: 100%;
    display: flex;
    justify-content: center;
    .b-aspect {
      width: 300px;
      height: 54px;
      .b-skeleton-img {
        border-radius: 30px;
      }
    }
  }
}
</style>
